"use client";

import React, { useState, useEffect, useRef, useMemo } from 'react';
import Link from 'next/link';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Pagination, Navigation } from 'swiper/modules';
import { useQuery } from '@apollo/client';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { GET_BANNERS } from '@/graphql/queries';
import { AWS_URL } from '@/constants';

const getImageUrl = (fileObj) => {
  if (!fileObj) return null;
  if (fileObj.path) return `${AWS_URL}${fileObj.path}`;
  return null;
};

const redirectOverrides = {
  1: '/shop',
  2: '/shop/best-sellers',
  3: '/new-arrivals',
};

const HeroSlideSection = () => {
  const [isClient, setIsClient] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const { data, loading, error } = useQuery(GET_BANNERS, {
    variables: { where: { type: { _eq: "BANNER" } } },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    setIsClient(true);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const allMeta = data?.tenant_org_web_configuration
    ?.flatMap((config) => config.web_configuration_meta) || [];

  const banners = useMemo(() => {
    if (!allMeta || allMeta.length === 0) return [];

    return allMeta
      .map((meta) => {
        const fileToUse = isMobile
          ? meta.mobile_file
          : (meta.file || meta.mobile_file);
        return { ...meta, resolvedFile: fileToUse };
      })
      .filter((meta) => {
        // CRITICAL: On mobile, ONLY show banners that have mobile_file
        // Never fallback to desktop file on mobile
        if (isMobile) {
          return !!meta.mobile_file;
        }
        // On desktop, show if file exists
        return !!meta.resolvedFile;
      });
  }, [isMobile, allMeta]);

  if (!isClient || loading) return null;
  if (error || banners.length === 0) return null;

  return (
    <div style={{ position: 'relative', width: '100%', overflow: 'hidden' }} className={isMobile ? '' : 'mt-8'}>
      <Swiper
        key={isMobile ? 'mobile' : 'desktop'}
        modules={[Autoplay, EffectFade, Pagination, Navigation]}
        effect="fade"
        speed={1500}
        autoplay={{ delay: 4000, disableOnInteraction: false }}
        loop={true}
        pagination={{
          clickable: true,
          bulletClass: 'swiper-pagination-bullet hero-pagination-bullet',
          bulletActiveClass: 'swiper-pagination-bullet-active hero-pagination-bullet-active',
        }}
        navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
        onSwiper={(swiper) => {
          setTimeout(() => {
            if (swiper.params && swiper.params.navigation) {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
              swiper.navigation.destroy();
              swiper.navigation.init();
              swiper.navigation.update();
            }
          });
        }}
        style={{ width: '100%' }}
      >
        {banners.map((banner, index) => {
          const src = getImageUrl(banner.resolvedFile);
          const href = redirectOverrides[index + 1] ?? banner.redirection_url ?? '/';

          return (
            <SwiperSlide key={index}>
              <Link href={href} style={{ display: 'block', width: '100%' }}>
                {src ? (
                  <img
                    src={src}
                    alt={`Banner ${index + 1}`}
                    style={{ width: '100%', height: 'auto', display: 'block', maxWidth: '100%' }}
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                ) : (
                  <div style={{ width: '100%', paddingTop: '33%', background: '#f3f3f3' }} />
                )}
              </Link>
            </SwiperSlide>
          );
        })}
      </Swiper>

      <style>{`
        .hero-pagination-bullet {
          width: 10px;
          height: 10px;
          background: rgba(255, 255, 255, 0.5);
          opacity: 0.6;
          transition: all 0.3s ease;
        }
        .hero-pagination-bullet-active {
          width: 24px;
          border-radius: 5px;
          background: rgba(255, 255, 255, 0.9);
          opacity: 1;
        }
        .swiper-pagination {
          bottom: 16px !important;
          z-index: 30;
        }
      `}</style>
    </div>
  );
};

export default HeroSlideSection;